import type { UndoableState } from "../../vendor/src/features/remotion-editor/editor/state/types";
import type { AudioItem } from "../../vendor/src/features/remotion-editor/editor/items/audio/audio-item-type";
import type { VideoItem } from "../../vendor/src/features/remotion-editor/editor/items/video/video-item-type";

function sameWindow(video: VideoItem, audio: AudioItem) {
  return (
    video.from === audio.from &&
    video.durationInFrames === audio.durationInFrames &&
    video.videoStartFromInSeconds === audio.audioStartFromInSeconds &&
    video.playbackRate === audio.playbackRate &&
    video.decibelAdjustment === audio.decibelAdjustment &&
    video.audioFadeInDurationInSeconds === audio.audioFadeInDurationInSeconds &&
    video.audioFadeOutDurationInSeconds ===
      audio.audioFadeOutDurationInSeconds &&
    JSON.stringify(video.audioFadeWindow ?? null) ===
      JSON.stringify(audio.audioFadeWindow ?? null)
  );
}
/** Restore the embedded soundtrack; remove only an unchanged, identified detached copy. */
export function restoreVideoAudio(
  document: UndoableState,
  selected: readonly string[],
): UndoableState {
  const ids = [...new Set(selected)];
  const videos = ids
    .map((id) => document.items[id])
    .filter((item): item is VideoItem => item?.type === "video");
  if (videos.length !== 1)
    throw Error("원음을 복구할 영상 하나를 선택해 주세요.");
  const video = videos[0]!;
  const track = document.tracks.find((t) => t.items.includes(video.id));
  if (!track || track.locked) throw Error("영상 트랙의 잠금을 풀어 주세요.");
  const source = document.assets[video.assetId];
  if (source?.type !== "video" || !source.hasAudioTrack || !source.remoteUrl)
    throw Error("이 영상에는 복구할 원음이 없습니다.");
  if (!video.muteAudio) throw Error("이미 영상 원음이 켜져 있습니다.");
  const active = new Set<string>();
  const visit = (id: string) => {
    if (active.has(id)) return;
    active.add(id);
    const item = document.items[id];
    if (item?.type === "group") item.children.forEach(visit);
  };
  document.tracks.flatMap((t) => t.items).forEach(visit);
  const audio = Object.values(document.items).filter(
    (item): item is AudioItem => item.type === "audio" && active.has(item.id),
  );
  const linked = audio.filter((item) => item.timelineSourceItemId === video.id);
  const explicit = audio.filter((item) => ids.includes(item.id));
  const candidates = [
    ...new Map(
      [...linked, ...explicit].map((item) => [item.id, item]),
    ).values(),
  ];
  const sameSource = (item: AudioItem) =>
    document.assets[item.assetId]?.remoteUrl === source.remoteUrl;
  // Historical detached tracks have no lineage. Require explicit selection rather than guessing.
  const unidentified = audio.filter(
    (item) =>
      sameSource(item) &&
      item.from < video.from + video.durationInFrames &&
      item.from + item.durationInFrames > video.from &&
      !candidates.includes(item),
  );
  if (unidentified.length)
    throw Error(
      "같은 원음의 오디오가 따로 있습니다. 결합할 영상과 오디오를 함께 선택해 주세요.",
    );
  if (candidates.length > 1)
    throw Error(
      "분리 음성이 여러 조각입니다. 음성 편집을 되돌리거나 필요한 오디오만 남긴 뒤 다시 시도해 주세요.",
    );
  for (const item of candidates) {
    if (
      Object.values(document.items).some(
        (other) => other.type === "group" && other.children.includes(item.id),
      )
    )
      throw Error("분리 음성의 그룹을 먼저 해제해 주세요.");

    const owner = document.tracks.find((t) => t.items.includes(item.id));
    if (!owner || owner.locked)
      throw Error("분리 음성 트랙의 잠금을 풀어 주세요.");
    if (
      !sameSource(item) ||
      !sameWindow(video, item) ||
      owner.muted !== track.muted ||
      owner.hidden !== track.hidden
    )
      throw Error(
        "분리 음성의 위치·속도·음량이 바뀌었습니다. 해당 편집을 되돌리거나 오디오를 따로 보관한 뒤 복구해 주세요.",
      );
    if (
      Object.values(document.transitions).some(
        (t) =>
          [item.id, video.id].includes(t.sourceItemId) ||
          [item.id, video.id].includes(t.targetItemId),
      )
    )
      throw Error(
        "전환 효과가 있는 작업입니다. 음성 복구 전에 효과 연결을 확인해 주세요.",
      );
  }
  const removed = new Set(candidates.map((item) => item.id));
  const items = {
    ...document.items,
    [video.id]: { ...video, muteAudio: false },
  };
  for (const id of removed) delete items[id];
  return {
    ...document,
    items,
    tracks: document.tracks.map((t) => ({
      ...t,
      items: t.items.filter((id) => !removed.has(id)),
    })),
  };
}
